import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  DateInput,
  SelectInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  Toolbar,
  SaveButton,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EquipmentFileUpload } from '../EquipmentFileUpload';

const AssignmentToolbar = () => (
  <Toolbar sx={{ 
    display: 'flex', 
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  }}>
    <SaveButton />
  </Toolbar>
);

interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const AssignmentDialog: React.FC<AssignmentDialogProps> = ({ 
  open, 
  onClose, 
  equipmentId,
  recordId,
  isEdit = false
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [files, setFiles] = useState([]);

  const conditionTypes = [
    { id: 'EXCELLENT', name: 'Excellent' },
    { id: 'GOOD', name: 'Good' },
    { id: 'FAIR', name: 'Fair' },
    { id: 'POOR', name: 'Poor' },
    { id: 'DAMAGED', name: 'Damaged' }
  ];

  // Load drivers when dialog opens
  useEffect(() => {
    if (open) {
      dataProvider.getList('drivers', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
        .then(({ data }) => {
          const driverChoices = data.map(driver => ({
            id: driver.id,
            name: driver.user?.name || `Driver ${driver.id}`
          }));
          setDrivers(driverChoices);
        })
        .catch((error) => {
          console.error('Error loading drivers:', error);
          notify('Error loading drivers', { type: 'error' });
        });
    }
  }, [open, dataProvider, notify]);

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      setLoading(true);
      dataProvider.getOne('equipment-assignments', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
          // Load files if they exist
          if (data.files) {
            setFiles(data.files);
          }
        })
        .catch((error) => {
          console.error('Error loading assignment record:', error);
          notify('Error loading assignment record', { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!isEdit) {
      setRecord(null);
      setFiles([]);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    setLoading(true);
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
      };
      
      console.log('Submitting assignment data:', formattedData);
      
      if (isEdit && recordId) {
        await dataProvider.update('equipment-assignments', {
          id: recordId,
          data: formattedData,
          previousData: record || {}
        });
        notify('Assignment record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-assignments', {
          data: formattedData
        });
        notify('Assignment record created successfully', { type: 'success' });
      }
      
      refresh();
      onClose();
    } catch (error) {
      console.error('Assignment submission error:', error);
      notify(`Error ${isEdit ? 'updating' : 'creating'} assignment record: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        {isEdit ? 'Edit Assignment' : 'Add Assignment Record'}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm 
          onSubmit={handleSubmit} 
          record={record || { 
            assignedDate: new Date().toISOString().split('T')[0] 
          }}
          toolbar={<AssignmentToolbar />}
          sx={{
            '& .RaSimpleForm-input': { width: '100%', maxWidth: '100%' },
            '& .MuiFormControl-root': { marginBottom: 0, width: '100%' }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <SelectInput source="driverId" choices={drivers} validate={[required()]} label="Driver" variant="outlined" size="small" fullWidth />
            <SelectInput source="condition" choices={conditionTypes} label="Condition" variant="outlined" size="small" fullWidth />
            <DateInput source="assignedDate" validate={[required()]} label="Assigned Date" variant="outlined" size="small" fullWidth />
            <NumberInput source="mileageOut" label="Mileage Out" variant="outlined" size="small" fullWidth />
            <DateInput source="returnedDate" label="Returned Date" variant="outlined" size="small" fullWidth />
            <NumberInput source="mileageIn" label="Mileage In" variant="outlined" size="small" fullWidth />
            <TextInput source="notes" multiline rows={3} label="Notes" variant="outlined" size="small" fullWidth />
          </Box>
        </SimpleForm>
        
        {/* File Attachments Section */}
        {isEdit && recordId && (
          <Box sx={{ mt: 3 }}>
            <EquipmentFileUpload
              entityType="assignment"
              entityId={recordId}
              files={files}
              onFilesChange={setFiles}
              title="Assignment Documents"
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};